import { useNavigate } from "@tanstack/react-router";
import { MoreHorizontal, X } from "lucide-react";
import type { ReactNode } from "react";
import { NOTIFICATION_CATEGORY_TOKENS } from "@/features/notifications/components/notification-category-tokens";
import {
  useMarkNotificationRead,
  useSoftDeleteNotification,
} from "@/features/notifications/hooks/use-notification-mutations";
import { notificationDeepLink } from "@/features/notifications/lib/notification-deep-link";
import type { NotificationRow as NotificationRowDto } from "@/features/notifications/schemas/notification.schema";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { cn } from "@/shared/lib/cn";

interface Props {
  workspaceId: string;
  workspaceSlug: string;
  notification: NotificationRowDto;
  /// When true, the row is rendered in the compact dropdown layout (no
  /// kebab; click marks read + closes the popover). False = inbox page.
  compact?: boolean;
  /// Closes the parent popover after a click (dropdown variant only).
  onAfterClick?: () => void;
}

const RELATIVE_FMT = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffSec = Math.round((then - now) / 1000);
  const absSec = Math.abs(diffSec);
  if (absSec < 60) return RELATIVE_FMT.format(diffSec, "second");
  if (absSec < 3600) return RELATIVE_FMT.format(Math.round(diffSec / 60), "minute");
  if (absSec < 86400) return RELATIVE_FMT.format(Math.round(diffSec / 3600), "hour");
  return RELATIVE_FMT.format(Math.round(diffSec / 86400), "day");
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/// Single inbox / dropdown row. Renders: actor avatar (or category icon
/// fallback) + title + relative time + (optional) kebab dismiss.
/// Clicking the row marks-read + navigates to the deep link.
export function NotificationRow({
  workspaceId,
  workspaceSlug,
  notification,
  compact = false,
  onAfterClick,
}: Props): ReactNode {
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead(workspaceId);
  const dismiss = useSoftDeleteNotification(workspaceId);
  const token = NOTIFICATION_CATEGORY_TOKENS.find((t) => t.category === notification.category);
  const Icon = token?.icon ?? null;
  const actor = notification.actor;
  const href = notificationDeepLink(workspaceSlug, notification);

  async function onClick() {
    if (!notification.isRead) {
      markRead.mutate(notification.id);
    }
    onAfterClick?.();
    void navigate({ to: href });
  }

  return (
    <div
      className={cn(
        "group flex items-start gap-3 px-3 py-2.5 transition-colors",
        notification.isRead ? "bg-surface" : "bg-surface-subtle/40",
        "hover:bg-surface-subtle",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex flex-1 items-start gap-3 text-left outline-none focus-visible:shadow-ring-focus rounded-md"
        aria-label={`${notification.title}, ${notification.isRead ? "read" : "unread"}`}
      >
        {actor ? (
          <Avatar className="size-8 shrink-0">
            <AvatarImage src={actor.avatarUrl ?? undefined} alt="" />
            <AvatarFallback className="bg-pulse-soft text-3xs text-text-brand-strong">
              {initials(actor.displayName)}
            </AvatarFallback>
          </Avatar>
        ) : Icon ? (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-surface-subtle">
            <Icon aria-hidden="true" className="size-4 text-text-tertiary" />
          </span>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p
            className={cn(
              "text-sm leading-snug",
              notification.isRead ? "text-text-secondary" : "text-text-primary font-medium",
            )}
          >
            {notification.title}
          </p>
          {notification.message ? (
            <p className="line-clamp-2 text-xs text-text-tertiary">{notification.message}</p>
          ) : null}
          <span className="text-3xs text-text-tertiary">
            {relativeTime(notification.createdAt)}
          </span>
        </div>
      </button>
      {!compact ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              // Touch devices never fire hover, so the kebab must be
              // discoverable without it (UX P2-A). Subdue at rest, fade
              // to full opacity on pointer hover for desktop polish.
              className="opacity-60 transition-opacity hover:opacity-100 group-hover:opacity-100 focus-visible:opacity-100"
              aria-label={`More options for notification: ${notification.title}`}
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!notification.isRead ? (
              <DropdownMenuItem onSelect={() => markRead.mutate(notification.id)}>
                Mark as read
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onSelect={() => dismiss.mutate(notification.id)}
              className="text-danger-fg"
            >
              <X aria-hidden="true" className="mr-1.5 size-3.5" />
              Dismiss
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
